import {mthen} from '@freckle/maybe'
import DateOptions from '@freckle/educator-entities/ts/common/helpers/date-options'
import {makeDateOptions} from '@freckle/educator-entities/ts/common/helpers/date-options'
import {Products} from '@freckle/educator-entities/ts/common/helpers/products'
import {useAsync, useAsyncOnceDefined} from '@freckle/educator-entities/ts/common/hooks/use-async'
import {useCourseMemberships} from '@freckle/educator-entities/ts/common/hooks/use-course-memberships'
import {isMathPremium as isMathPremiumF} from '@freckle/educator-entities/ts/common/logic/premium-access'
import {fetchClassroomGenericAssignmentSessions} from '@freckle/educator-entities/ts/common/models/generic-assignment-session'
import {fetchFactPracticeOperations} from '@freckle/educator-entities/ts/math/fact-practice/api'
import {fetchFactPracticeLevelsForCourse} from '@freckle/educator-entities/ts/math/fact-practice/models/math-fact-practice-course-levels'
import {WithResources} from '@freckle/classroom/ts/common/components/with-resources'
import {useCurrentCourseId} from '@freckle/classroom/ts/hooks/use-current-course-id'
import {useLanguage} from '@freckle/classroom/ts/hooks/use-language'
import {useQueryParamAsDate, useQueryParamAsNum} from '@freckle/classroom/ts/hooks/use-params'
import {usePremiumLicenses} from '@freckle/classroom/ts/hooks/use-premium-licenses'
import {useStudents} from '@freckle/classroom/ts/hooks/use-students'
import {useTeacher} from '@freckle/classroom/ts/hooks/use-teacher'
import {forCourse} from '@freckle/classroom/ts/users/collections/students'
import {FactPracticeReports} from './fact-practice'

export default function FactPracticeReportHandler() {
  const courseId = useCurrentCourseId()
  const studentId = useQueryParamAsNum('student-id')
  const {from: mFrom, to: mTo, dateOption: mDateOption} = useQueryParamAsDate()
  const teacher = useTeacher()
  const courseMemberships = useCourseMemberships()
  const students = useStudents()
  const premiumLicenses = usePremiumLicenses()
  const language = useLanguage()

  const allMathFactPracticeOperations = useAsync(
    () => fetchFactPracticeOperations(language),
    'fetchFactPracticeOperations',
    [language],
  )

  const mathFactPracticeCourseLevels = useAsync(
    () => fetchFactPracticeLevelsForCourse(courseId),
    'fetchFactPracticeLevelsForCourse',
    [courseId],
  )

  const dateOptions = useAsyncOnceDefined(
    ({premiumLicenses}) =>
      Promise.resolve(
        makeDateOptions(
          mDateOption,
          mFrom,
          mTo,
          isMathPremiumF(premiumLicenses) ? DateOptions.ThisMonth : DateOptions.ThisWeek,
        ),
      ),
    'makeDateOptions',
    [mDateOption, mFrom, mTo, premiumLicenses.data],
    {premiumLicenses},
  )

  const completedAt = {data: mthen(dateOptions.data, ({from, to}) => ({gte: from, lt: to}))}

  const genericSessions = useAsyncOnceDefined(
    ({teacher, completedAt}) =>
      fetchClassroomGenericAssignmentSessions(courseId, [Products.MathFactPractice], {
        mathStandardSetId: teacher.mathStandardSetId,
        elaStandardSetId: teacher.elaStandardSetId,
        completedAt,
        // Don't fetch for specific student ID because expanding other
        // students is expected
      }),
    'fetchClassroomGenericAssignmentSessions',
    [
      courseId,
      [Products.MathFactPractice],
      mthen(teacher.data, teacher => ({
        mathStandardSetId: teacher.mathStandardSetId,
        elaStandardSetId: teacher.elaStandardSetId,
        completedAt,
      })),
    ],
    {teacher, completedAt},
  )

  return (
    <WithResources
      resources={{
        students,
        courseMemberships,
        premiumLicenses,
        teacher,
        allMathFactPracticeOperations,
        mathFactPracticeCourseLevels,
        genericSessions,
        dateOptions,
      }}
      render={({
        students,
        courseMemberships,
        premiumLicenses,
        allMathFactPracticeOperations,
        mathFactPracticeCourseLevels,
        genericSessions,
        dateOptions: {from, to, dateOption},
      }) => {
        // Filter students for this course
        const courseStudents = forCourse(students, courseId, courseMemberships)
        const isMathPremium = isMathPremiumF(premiumLicenses)

        return (
          <FactPracticeReports
            genericSessions={genericSessions}
            mathFactPracticeCourseLevels={mathFactPracticeCourseLevels}
            students={courseStudents}
            initialStudentId={studentId}
            isPremium={isMathPremium}
            premiumLicenses={premiumLicenses}
            dateOption={dateOption}
            from={from}
            to={to}
            allMathFactPracticeOperations={allMathFactPracticeOperations}
            language={language}
          />
        )
      }}
    />
  )
}
